/**
 * @class DataLayerTracker
 * @classdesc Tracks and pushes data from HTML elements into the dataLayer object for analytics.
 *
 * @author Dreamers of Day
 */
export default class DataLayerTracker {
    /**
     * The event name to be pushed into the dataLayer when filters are applied.
     * @type {string}
     */
    event = 'select_geography_applied';

    /**
     * Indicates whether pushing data to the dataLayer is allowed.
     * @type {boolean}
     */
    pushingAllowed = false;

    /**
     * Stores references to static DOM elements that are being tracked.
     * @type {Object<string, HTMLElement|null>}
     */
    staticElements = {};

    /**
     * Stores selectors for dynamic DOM elements to be queried and tracked.
     * @type {Object<string, string>}
     */
    dynamicElements = {};

    /**
     * Stores elements which trigger filterPush.
     * Array has HTML element as the key to get array value ( { <HTMLNode> : value } )
     */
    triggerElements = new WeakMap();

    /**
     * Constructs a new instance of DataLayerTracker.
     * Initializes static and dynamic elements to be tracked.
     *
     * @param {Object<string, string>} staticElements - A map of element keys and their corresponding CSS selectors.
     * @param {Object<string, string>} [dynamicElements={}] - A map of dynamic element keys and their corresponding CSS selectors.
     * @param triggerElements
     */
    constructor(staticElements, dynamicElements = {}, triggerElements = {}) {
        // Iterate over staticElements to find and store DOM references
        Object.entries(staticElements).forEach(([key, value]) => {
            const element = document.querySelector(value);
            if (element) {
                this.staticElements[key] = element;
            } else {
                // Set to null if the element is not found in the DOM
                this.staticElements[key] = null;
            }
        });

        // Initialize dynamic elements
        this.dynamicElements = dynamicElements;

        // Initialize trigger elements
        Object.entries(triggerElements).forEach(([key, value]) => {
            const listener = value.eventListener ?? null;
            const selector = value.selector ?? null;

            if (!listener || !selector) {
                return;
            }

            const elements = document.querySelectorAll(selector);
            if (!elements.length) {
                return;
            }

            elements.forEach((el) => {
                el.addEventListener(listener, this.triggerPush.bind(this));

                // Set a unique key in the WeakMap for this element
                this.triggerElements.set(el, key);
            });
        });
    }

    /**
     * Pushes filters and element data into the dataLayer for analytics tracking.
     * The method gathers values from static and dynamic elements and sends them if conditions are met.
     */
    pushFilters(addValues = {}) {
        // Exit early if pushing is not allowed
        if (!this.pushingAllowed) {
            return;
        }

        // Verify if the global dataLayer object exists
        if (typeof window.dataLayer !== 'object') {
            console.log('"dataLayer" object is absent');
            return;
        }

        let dataLayerArray = {}; // Object to store data to push
        let sendFlag = false; // Tracks whether we have valid data to send

        // Iterate over static elements
        Object.entries(this.staticElements).forEach(([key, value]) => {
            if (value && value.tagName === 'SELECT') {
                dataLayerArray[key] = value.options[value.selectedIndex].text;
                sendFlag = true;
            } else {
                dataLayerArray[key] = '';
            }
        });

        // Iterate over dynamic elements and gather data
        Object.entries(this.dynamicElements).forEach(([key, value]) => {
            const element = document.querySelector(value);
            if (element) {
                dataLayerArray[key] = element.textContent.trim();
            } else {
                dataLayerArray[key] = '';
            }
        });

        // Exit if no valid data has been flagged for sending
        if (!sendFlag) {
            return;
        }

        //
        const merged = Object.assign({}, dataLayerArray, addValues);

        // Add the event name to the dataLayer array
        merged['event'] = this.event;

        console.log(merged);

        // Push the constructed dataLayer object if the dataLayer exists
        if (typeof window.dataLayer === 'object') {
            window.dataLayer.push(merged);
        }
    }

    /**
     * Custom event handler that retrieves text content from the triggering element,
     * associates it with a unique key, and pushes the result into filters.
     *
     * @param {Event} event - The event object from the triggered listener.
     */
    triggerPush(event) {
        const element = event.currentTarget;
        // Get the unique key for this element from the WeakMap
        const triggerId = this.triggerElements.get(element);

        // Get the trimmed text content of the element, defaulting to 1 ( true ) if empty
        const textContent = element.textContent.trim() || 1;

        // Create an object to store the filter with the key and text content
        const filterData = {};
        filterData[triggerId] = textContent;

        // Push the filter data to the filters stack (or wherever the data is stored)
        this.pushFilters(filterData);
    }

    /**
     * Allows data to be pushed into the dataLayer by setting the pushingAllowed flag to true.
     */
    allowPushing() {
        this.pushingAllowed = true;
    }

    /**
     * Prevents data from being pushed into the dataLayer by setting the pushingAllowed flag to false.
     */
    forbidPushing() {
        this.pushingAllowed = false;
    }
}
